<script setup>
import { ref, watch, nextTick } from 'vue'
import DropdownMenu from '@/components/Dropdown/DropdownMenu.vue'
import { useAnchoredMenu } from '@/composables/useAnchoredMenu'

// Top values + cardinality for one table column. Counts come from a `provider`
// the host supplies, so this component is agnostic to whether they were
// aggregated by OpenSearch (Event Search, over the whole index) or counted in
// the browser (Selector Search, over the rows on screen).

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  field: {
    type: String,
    required: true
  },
  // Display name for the header; falls back to the raw field name.
  label: {
    type: String,
    default: null
  },
  // (field) => { terms: [{value, count}], cardinality }. May be async, and may
  // throw — the message surfaces in the error state.
  provider: {
    type: Function,
    required: true
  },
  anchorEl: {
    type: Object,
    default: null
  },
  // Whether the cardinality footer links onward to a fuller aggregations view.
  // Off where there is nowhere to go, leaving it a plain stat.
  expandable: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['add-filter', 'close', 'navigate-aggregations'])

const loading = ref(false)
const error = ref(null)
const terms = ref([])
const cardinality = ref(null)
const menuComp = ref(null)

// Must be initialized above the watches below: hosts mount this component with
// `show` already true, so the immediate watch fetches during setup.
let fetchToken = 0

const { menuStyle, reposition } = useAnchoredMenu({
  anchorRef: () => props.anchorEl,
  menuRef: () => menuComp.value?.el,
  isOpen: () => props.show,
  placement: 'below',
  // Flush against the header: the popover stays open by checking that
  // mouseleave's relatedTarget is inside it, so any gap to cross would close it.
  gap: 0,
  onDismiss: () => emit('close')
})

async function refresh() {
  await fetchStats()
  await nextTick()
  reposition()
}

// Fetch when shown
watch(() => props.show, async (isShown) => {
  if (isShown && props.field) await refresh()
}, { immediate: true })

// Reposition when the anchor moves to another column header
watch(() => props.anchorEl, async () => {
  if (!props.show) return
  await nextTick()
  reposition()
})

// Refetch when switching columns, and when the provider itself changes — hosts
// rebuild it as a computed, so a new identity means the underlying data moved
// (a new query scope, or another batch of rows landing).
watch([() => props.field, () => props.provider], async () => {
  if (props.show && props.field) await refresh()
})

// Fetch stats for the current field
async function fetchStats() {
  // Providers can be swapped mid-flight (rows arriving, query changing), so tag
  // each fetch and drop any result a newer one has superseded.
  const token = ++fetchToken
  loading.value = true
  error.value = null
  // The previous column's values stay mounted under the loading overlay. They
  // aren't visible through it, but they hold the panel's height, so hovering
  // along a header row swaps content in place instead of collapsing the panel
  // to its header and regrowing. The token guard below is what keeps a
  // superseded result from landing — clearing here never did that.

  try {
    const result = await props.provider(props.field)
    if (token !== fetchToken) return
    terms.value = result?.terms || []
    cardinality.value = result?.cardinality ?? null
  } catch (err) {
    if (token !== fetchToken) return
    error.value = err?.message || 'Failed to load aggregations'
    console.error('Column stats error:', err)
  } finally {
    if (token === fetchToken) loading.value = false
  }
}

// Add filter from the + / - buttons
function addFilter(value, exclude = false) {
  if (props.field === '@timestamp') {
    emit('add-filter', { field: props.field, value, operator: 'time_range' })
    return
  }
  emit('add-filter', { field: props.field, value, operator: exclude ? 'exclude' : 'equals' })
}

// Format term value for display
function formatTermValue(value) {
  if (props.field === '@timestamp') {
    const n = Number(value)
    if (!isNaN(n) && n > 1e9) {
      return new Date(n).toISOString().replace('T', ' ').slice(0, -5)
    }
  }
  return String(value)
}

// Navigate to aggregations view with this field
function navigateToAggregations() {
  emit('navigate-aggregations', props.field)
  emit('close')
}

// Format large numbers
function formatCount(count) {
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1) + 'M'
  }
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K'
  }
  return count.toLocaleString()
}
</script>

<template>
  <DropdownMenu
    ref="menuComp"
    class="column-stats-popover"
    :class="{ 'is-reserving': loading && terms.length === 0 }"
    :show="show"
    :header-label="label || field"
    :loading="loading"
    loading-text="Loading..."
    :menu-style="menuStyle"
    @mouseleave="$emit('close')"
  >
    <!-- Error State -->
    <div v-if="error" class="error-state">
      <span>{{ error }}</span>
    </div>

    <template v-else>
      <div v-if="terms.length === 0 && !loading" class="empty-state">
        No values found
      </div>
      <div v-else class="terms-list">
        <div
          v-for="term in terms"
          :key="term.value"
          class="term-item"
        >
          <span class="term-value" :title="term.value">{{ formatTermValue(term.value) }}</span>
          <span class="term-count">{{ formatCount(term.count) }}</span>
          <button class="filter-btn filter-for" title="Filter for value" @click.stop="addFilter(term.value)">
            <font-awesome-icon :icon="['fas', 'plus']" />
          </button>
          <button class="filter-btn filter-out" title="Filter out value" @click.stop="addFilter(term.value, true)">
            <font-awesome-icon :icon="['fas', 'minus']" />
          </button>
        </div>
      </div>

      <!-- Always a footer stat; only a link where there's an aggregations view
           to drill into. -->
      <template v-if="cardinality !== null">
        <div class="dropdown-divider"></div>
        <div
          class="cardinality-section"
          :class="{ clickable: expandable }"
          @click="expandable && navigateToAggregations()"
        >
          <span class="cardinality-value">{{ formatCount(cardinality) }}</span>
          <span class="cardinality-label">unique values</span>
          <font-awesome-icon v-if="expandable" class="cardinality-arrow" :icon="['fas', 'arrow-right']" />
        </div>
      </template>
    </template>
  </DropdownMenu>
</template>

<!-- The panel is DropdownMenu's own element, so this component's scope attribute
     doesn't reach it. Namespaced under the popover's class so nothing leaks. -->
<style>
/* Bound the width: values are arbitrary strings, and a shrink-to-fit panel would
   stretch to the longest of them. .term-value ellipsises within this. */
.dropdown-panel.column-stats-popover {
  min-width: 260px;
  max-width: 320px;
}

/* Only on the first open, when there are no previous values to hold the height
   (see fetchStats). Without it the panel measures header-height, useAnchoredMenu
   anchors it as if that's how tall it is, and the real content then relocates
   it — the panel jumps rather than grows.

   390px is a full panel: a 10-term list (both providers cap at size=10, and
   .terms-list bounds that at 300px) plus the header and the cardinality footer.
   High-cardinality columns — IPs, ports, timestamps — hit that cap and land
   exactly here. A low-cardinality column settles shorter, rising from a fixed
   top edge. */
.dropdown-panel.column-stats-popover.is-reserving {
  min-height: 390px;
}
</style>

<style scoped>
/* Scoped to this component; the markup lives in DropdownMenu's slot but keeps
   this component's scope attribute, so the styles follow it after teleport. */
.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  color: var(--color-error);
  font-size: 0.85rem;
}

.empty-state {
  color: var(--text-faint);
  font-size: 0.8rem;
  font-style: italic;
  padding: 0.75rem 1.25rem;
}

.terms-list {
  display: flex;
  flex-direction: column;
  max-height: 300px;
  overflow-y: auto;
}

.term-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: var(--radius-md);
  transition: var(--transition-fast);
}

.term-item:hover {
  background: var(--surface-overlay);
}

.term-value {
  flex: 1;
  font-size: 0.8rem;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Cutive Mono', monospace;
}

.term-count {
  font-size: 0.75rem;
  color: var(--text-muted);
  font-weight: 500;
}

.filter-btn {
  background: none;
  border: none;
  color: var(--text-faint);
  cursor: pointer;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-xs);
  transition: var(--transition-fast);
  font-size: 0.7rem;
}

.filter-btn.filter-for:hover {
  color: var(--accent);
  background: var(--accent-soft-2);
}

.filter-btn.filter-out:hover {
  color: var(--color-error);
  background: var(--color-error-soft);
}

/* Aligned to the header's inset rather than the inset pills above, so the stat
   reads as a footer for the whole panel. */
.cardinality-section {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem 0.35rem;
  transition: var(--transition-fast);
}

.cardinality-section.clickable {
  cursor: pointer;
}

.cardinality-section.clickable:hover {
  background: var(--accent-soft-2);
}

.cardinality-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--accent);
}

.cardinality-label {
  font-size: 0.8rem;
  color: var(--text-faint);
  flex: 1;
}

.cardinality-arrow {
  font-size: 0.75rem;
  color: var(--text-faint);
  opacity: 0;
  transition: var(--transition-fast);
  flex-shrink: 0;
}

.cardinality-section.clickable:hover .cardinality-arrow {
  opacity: 1;
  color: var(--accent);
}

.terms-list::-webkit-scrollbar {
  width: 6px;
}

.terms-list::-webkit-scrollbar-track {
  background: transparent;
}

.terms-list::-webkit-scrollbar-thumb {
  background: var(--surface-control);
  border-radius: var(--radius-xs);
}
</style>
